import { FileUp, Sparkles } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
} from "react";

import { cn } from "@link/ui";

interface FileDropOptions {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * File-only drag handlers for a complete chat surface. The depth counter keeps
 * the overlay stable while the cursor crosses children inside the message pane.
 */
export function useChatFileDrop({
  onFiles,
  disabled = false,
}: FileDropOptions) {
  const [active, setActive] = useState(false);
  const dragDepth = useRef(0);

  const reset = useCallback(() => {
    dragDepth.current = 0;
    setActive(false);
  }, []);

  useEffect(() => {
    // A file dropped outside the active chat must not navigate away from Link.
    // Only the chat's own handler queues files; text/link drags are untouched.
    const preventFileNavigation = (event: globalThis.DragEvent) => {
      if (event.dataTransfer && Array.from(event.dataTransfer.types).includes("Files")) {
        event.preventDefault();
        if (event.type === "drop") reset();
      }
    };
    document.addEventListener("dragover", preventFileNavigation);
    document.addEventListener("drop", preventFileNavigation);
    window.addEventListener("blur", reset);
    window.addEventListener("dragend", reset);
    return () => {
      document.removeEventListener("dragover", preventFileNavigation);
      document.removeEventListener("drop", preventFileNavigation);
      window.removeEventListener("blur", reset);
      window.removeEventListener("dragend", reset);
    };
  }, [reset]);

  const onDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current += 1;
    setActive(true);
  }, []);

  const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled ? "none" : "copy";
  }, [disabled]);

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (dragDepth.current === 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setActive(false);
  }, []);

  const onDrop = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    reset();
    if (!disabled && files.length > 0) onFiles(files);
  }, [disabled, onFiles, reset]);

  return {
    active,
    handlers: {
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd: reset,
    },
  };
}

export function ChatFileDropOverlay({
  active,
  disabled = false,
  destination,
}: {
  active: boolean;
  disabled?: boolean;
  destination: string;
}) {
  return (
    <div
      aria-hidden={!active}
      className={cn(
        "pointer-events-none absolute bottom-20 left-1/2 z-[70] flex w-[min(360px,calc(100%-2rem))] -translate-x-1/2 items-center gap-3 rounded-2xl border border-strawberry-200 bg-white/95 p-3.5 shadow-[0_18px_50px_rgba(218,82,128,0.22)] ring-1 ring-white/70 backdrop-blur-xl transition-all duration-200 ease-out",
        active
          ? "visible translate-y-0 scale-100 opacity-100"
          : "invisible translate-y-2 scale-95 opacity-0",
      )}
    >
      <div className="relative flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-strawberry-gradient text-white shadow-md shadow-strawberry-400/25">
        <FileUp className={cn("h-5 w-5", active && !disabled && "animate-bounce")} />
        <Sparkles className="absolute -right-1 -top-1 h-3 w-3 text-strawberry-400" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-cloud-800">
          {disabled ? "This chat is still getting ready" : "Drop it right here"}
        </p>
        <p className="mt-0.5 truncate text-xs text-cloud-500">
          {disabled
            ? "Uploads will be available in a moment."
            : `Upload to ${destination} · up to 25 MB per file`}
        </p>
      </div>
      {!disabled ? (
        <span className="flex-none rounded-full bg-strawberry-50 px-2.5 py-1 text-[10px] font-semibold text-strawberry-700 ring-1 ring-strawberry-200">
          Release
        </span>
      ) : null}
    </div>
  );
}

function isFileDrag(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer.types).includes("Files");
}
